// src/hooks/usePushNotifications.js
//
// Capacitor native push bridge. Web push (FCM via service worker) is handled
// by src/firebase/messaging.js + src/firebase-messaging-sw.js — that flow
// continues to work in browsers. This hook ONLY runs on Capacitor native
// builds (iOS / Android), where the web SW push API doesn't apply.
//
// Three responsibilities:
//   1. Request OS permission for push (system prompt)
//   2. Register native FCM token to users/{uid}/fcmTokens/{token} so the
//      backend `sendUserNotification` finds it alongside web tokens
//   3. When a push is TAPPED while the app is closed/backgrounded, parse
//      the URL from notification data and navigate via React Router
//
// Web (non-Capacitor) builds: dynamic import returns a no-op since
// '@capacitor/push-notifications' isn't bundled.
//
// Required:
//   npm install @capacitor/push-notifications
//   npx cap sync
//
//   Android: ensure google-services.json is in android/app/ (Capacitor +
//     Firebase Android template handles this — already done since FCM is
//     wired for web push on prod)
//   iOS: enable Push Notifications + Background Modes (Remote notifications)
//     capabilities in Xcode, plus an APNs auth key uploaded to Firebase
//     project settings (Apple Developer required — see compliance doc)

import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { doc, setDoc, serverTimestamp } from 'firebase/firestore';
import { PushNotifications } from '@capacitor/push-notifications';
import { Capacitor } from '@capacitor/core';
import { db } from '@/firebase/firebase';
import { useAuth } from '@/context/AuthContext';

const APP_HOST = 'app.getscrimly.com';

/** Pull a routable in-app path out of a push notification payload. */
function resolveTargetPath(notification) {
  const data = notification?.data || {};
  // Backend convention (see functions/core/channels/push.js): every push
  // includes data.url with a relative path or absolute URL.
  let raw = data.url || data.page || data.click_action || data.link || '/';

  // Some pushes pack the full https://app.getscrimly.com/... URL — strip
  // the host so React Router gets a relative path.
  try {
    if (raw.startsWith('http')) {
      const u = new URL(raw);
      if (u.host === APP_HOST) {
        return `${u.pathname}${u.search}${u.hash || ''}`;
      }
      // External URL on a different host — let it open in the system browser
      // by returning null (caller falls back to no navigation).
      return null;
    }
  } catch { /* not a URL — treat as path */ }

  return raw.startsWith('/') ? raw : `/${raw}`;
}

export default function usePushNotifications() {
  const navigate = useNavigate();
  const { firebaseUser } = useAuth();
  const uid = firebaseUser?.uid;
  // Only set up the listeners once per session — the @capacitor/push-
  // notifications plugin is global and double-registering creates dupes.
  const setupRef = useRef(false);

  // Live ref to the current uid. The registration callback closure runs
  // long after `useEffect` first fired — on first-install the bridge can
  // mount before auth resolves (uid=null), and reading `uid` directly
  // from the enclosing scope captures that stale null. Storing the live
  // value here lets the callback read the CURRENT uid at the moment FCM
  // hands us a token, even if that's seconds-or-minutes after setup.
  // Also stash the deferred token so a registration that races ahead of
  // sign-in still gets persisted as soon as the uid lands.
  const uidRef = useRef(uid);
  const pendingTokenRef = useRef(null);

  // Helper: write a token to users/{uid}/fcmTokens/{token}. Safe to call
  // multiple times for the same token — setDoc + merge is idempotent.
  const writeToken = async (currentUid, tokenValue) => {
    if (!currentUid || !tokenValue) return;
    try {
      await setDoc(
        doc(db, `users/${currentUid}/fcmTokens/${tokenValue}`),
        {
          token: tokenValue,
          userId: currentUid,
          updatedAt: serverTimestamp(),
          platform: 'capacitor_native',
          userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : 'capacitor',
        },
        { merge: true }
      );
      if (import.meta.env.DEV) console.info('[usePush] token registered');
    } catch (err) {
      console.warn('[usePush] token write failed', err);
    }
  };

  // Keep uidRef in lockstep with the current uid AND flush any token
  // that arrived before sign-in completed. This is what closes the
  // first-install race: on the second render where uid finally
  // becomes defined, we drain pendingTokenRef into Firestore.
  useEffect(() => {
    uidRef.current = uid;
    if (uid && pendingTokenRef.current) {
      const t = pendingTokenRef.current;
      pendingTokenRef.current = null;
      writeToken(uid, t);
    }
  }, [uid]);

  useEffect(() => {
    if (setupRef.current) return;
    let cancelled = false;
    let listeners = [];

    // Skip on web — the @capacitor/push-notifications plugin is a no-op
    // there, but we want web FCM (firebase/messaging.js) to own that
    // flow instead. Native iOS/Android only.
    if (!Capacitor.isNativePlatform()) {
      console.info('[usePush] web platform — skipping native push setup');
      return;
    }

    (async () => {
      console.info('[usePush] native platform detected:', Capacitor.getPlatform());

      try {
        // 1. Permission. Native dialog appears the first time.
        let perm = await PushNotifications.checkPermissions();
        console.info('[usePush] checkPermissions:', perm);
        if (perm.receive === 'prompt' || perm.receive === 'prompt-with-rationale') {
          perm = await PushNotifications.requestPermissions();
          console.info('[usePush] requestPermissions:', perm);
        }
        if (perm.receive !== 'granted') {
          console.warn('[usePush] permission not granted — aborting', perm.receive);
          return;
        }

        // 2. Add listeners FIRST, then call register(). The plugin can
        //    fire the `registration` event synchronously when FCM has a
        //    warm cached token from a prior install — if we register
        //    listeners after the call, the event has already fired into
        //    the void and we never see the token. This bit us through
        //    v1.1.3.
        listeners.push(await PushNotifications.addListener('registration', async (token) => {
          console.info('[usePush] registration event fired — token length:', token?.value?.length);
          if (cancelled) return;
          const t = token?.value;
          if (!t) {
            console.warn('[usePush] registration event but token value empty');
            return;
          }
          const currentUid = uidRef.current;
          if (!currentUid) {
            // Auth hasn't resolved yet. Stash the token; the uid-effect
            // above will flush it as soon as firebaseUser.uid lands.
            pendingTokenRef.current = t;
            console.info('[usePush] token deferred — awaiting auth');
            return;
          }
          await writeToken(currentUid, t);
        }));

        // 3. Registration error
        listeners.push(await PushNotifications.addListener('registrationError', (err) => {
          console.warn('[usePush] registrationError event:', err);
        }));

        // 4. Now kick off the FCM registration. Any `registration` /
        //    `registrationError` events fire AFTER both listeners are
        //    attached.
        console.info('[usePush] calling register()');
        await PushNotifications.register();
        console.info('[usePush] register() resolved — awaiting registration event');

        // 5. Push received while app is in foreground.
        //    On native, the OS doesn't auto-display these — the app decides.
        //    For now we just log; the existing in-app inbox already gets
        //    the corresponding users/{uid}/notifications doc via the
        //    sendUserNotification CF (same path as web).
        listeners.push(await PushNotifications.addListener('pushNotificationReceived', (notif) => {
          if (import.meta.env.DEV) console.info('[usePush] foreground push', notif);
          // Future: show an in-app toast so foreground users see the alert.
        }));

        // 6. Push TAPPED — the navigation event we care about.
        //    Fires when the user taps a notification while the app is
        //    backgrounded OR when the OS launches the app from a tap on
        //    the lock-screen / notification shade.
        listeners.push(await PushNotifications.addListener('pushNotificationActionPerformed', (event) => {
          const target = resolveTargetPath(event?.notification);
          if (target) {
            navigate(target, { replace: true });
          }
        }));

        // 7. Cold-start: if the app was launched FROM a push tap, the
        //    'pushNotificationActionPerformed' listener doesn't fire
        //    until next time. getDeliveredNotifications() doesn't capture
        //    the tap that launched the app, but the OS passes that info
        //    via the deep-link bridge if the FCM payload included data.url
        //    OR via the launch-extras. Both Android & iOS expose this
        //    through the App.getLaunchUrl() flow we already wired in
        //    useDeepLinks — no extra work needed here for cold starts.

        setupRef.current = true;
      } catch (err) {
        console.warn('[usePush] setup failed', err);
      }
    })();

    return () => {
      cancelled = true;
      // Detach listeners on unmount. .remove() returns a promise; we
      // fire-and-forget since the component is going away anyway.
      listeners.forEach((l) => { try { l?.remove?.(); } catch { /* noop */ } });
      listeners = [];
    };
  }, [uid, navigate]);
}
